import { Head, InfiniteScroll, router, useForm } from "@inertiajs/react";
import {
  BookOpen,
  CheckCircle,
  Globe,
  ImagePlus,
  Images,
  Key,
  Link2,
  Loader2,
  Palette,
  Plus,
  Search,
  Sparkles,
  Trash,
  Upload,
  X,
  Zap,
  ZoomIn,
} from "lucide-react";
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "sonner";

import { AddDomainModal } from "@/components/add-domain-modal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Kbd } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import AppLayout from "@/layouts/app-layout";
import { cn } from "@/lib/utils";
import { dashboardPath, domainPath, socialImagePath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: dashboardPath(),
  },
];

interface Domain {
  id: string;
  hostname: string;
  created_at: string;
  updated_at: string;
  customLogoUrl?: string | null;
  bg_color_hex?: string | null;
  text_color_hex?: string | null;
  verification_status?: "unverified" | "verified" | "pending" | "failed";
  verification_record_name?: string;
  verification_record_value?: string;
  verification_hostname?: string;
  verification_checked_at?: string | null;
  verified_at?: string | null;
}

export interface SocialImage {
  id: string;
  url: string;
  title?: string;
  status: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  is_root_image: boolean;
  image_url?: string;
  page_type: string | null;
  domain: string | null;
}

interface DashboardProps {
  domains: Domain[];
  active_claim: DomainClaim | null;
  active_social_images: SocialImage[];
  is_generating_new_images: boolean;
}

interface DomainClaim {
  id: number;
  status: "pending" | "verified" | "failed" | "expired" | "canceled";
  hostname: string;
  recordName: string;
  recordValue: string;
  expiresAt?: string | null;
  checkedAt?: string | null;
  verifiedAt?: string | null;
}

export default function Dashboard({
  domains,
  active_claim,
  active_social_images,
  is_generating_new_images,
}: DashboardProps) {
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SocialImage | null>(null);
  const [logoDomain, setLogoDomain] = useState<Domain | null>(null);
  const [brandColorsDomain, setBrandColorsDomain] = useState<Domain | null>(null);
  const [verifyDomain, setVerifyDomain] = useState<Domain | null>(null);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [isBrandColorsDialogOpen, setIsBrandColorsDialogOpen] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [search, setSearch] = useState("");
  const debounceTimeoutRef = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useHotkeys("ctrl+k, meta+k", () => {
    setSearch("");
    searchInputRef.current?.focus();
  });

  const {
    data: logoFormData,
    setData: setLogoFormData,
    patch: patchLogoFormData,
    processing: isLogoSubmitting,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    reset: resetLogoFormData,
  } = useForm({
    custom_logo: null as File | null,
    custom_logo_name: "",
    custom_logo_preview: null as string | null,
  });

  const {
    data: brandColorsFormData,
    setData: setBrandColorsFormData,
    patch: patchBrandColorsFormData,
    processing: isBrandColorsSubmitting,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    reset: resetBrandColorsFormData,
  } = useForm({
    bg_color_hex: brandColorsDomain?.bg_color_hex || "#0B1220",
    text_color_hex: brandColorsDomain?.text_color_hex || "#FFFFFF",
  });

  const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

  useEffect(() => {
    if (debounceTimeoutRef.current !== null) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = window.setTimeout(() => {
      const params = search.length > 0 ? { q: search } : {};
      router.get(dashboardPath(), params, { preserveState: true });
    }, 500);

    return () => {
      if (debounceTimeoutRef.current !== null) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [search]);

  const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const formatPageType = (pageType?: string | null) => {
    if (!pageType) return "—";
    return pageType
      .split("_")
      .map((segment) => (segment.length > 0 ? `${segment[0].toUpperCase()}${segment.slice(1)}` : segment))
      .join(" ");
  };

  const isMac = navigator.userAgent.includes("Mac");
  const hasUnverifiedDomains = domains.some((domain) => domain.verification_status !== "verified");
  const firstUnverifiedDomain = domains.find((domain) => domain.verification_status !== "verified") || null;

  const getStatusBadge = (status: string) => {
    const normalized = status.replace(/_/g, " ");

    if (status === "completed") {
      return <Badge variant="success">Completed</Badge>;
    }
    if (status === "failed") {
      return <Badge variant="destructive">Failed</Badge>;
    }
    if (["pending", "processing", "screenshot", "metadata", "analyzing", "generating", "finalizing"].includes(status)) {
      return <Badge variant="secondary">{normalized}</Badge>;
    }

    return <Badge variant="outline">{normalized}</Badge>;
  };

  const getDomainVerificationBadge = (status?: Domain["verification_status"]) => {
    switch (status) {
      case "verified":
        return <Badge variant="success">Verified</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "pending":
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">Unverified</Badge>;
    }
  };

  const handleImageView = (image: SocialImage) => {
    setSelectedImage(image);
  };

  const handleImageViewClose = () => {
    setSelectedImage(null);
  };

  const handleImageDelete = (image: SocialImage) => {
    if (confirm("Are you sure you want to delete this social image?")) {
      router.delete(socialImagePath(image.id), {
        preserveScroll: true,
      });
      toast.success("Social image deleted successfully");
    }
  };

  const handleDomainDelete = (domain: Domain) => {
    if (
      confirm(
        "Are you sure you want to delete this domain? This will remove all social images generated for this domain too.",
      )
    ) {
      router.delete(domainPath(domain.id), {
        preserveScroll: true,
      });
      toast.success("Domain deleted successfully");
    }
  };

  const handleSetCustomLogo = (domain: Domain) => {
    setLogoDomain(domain);
    resetLogoFormData();
    setIsLogoDialogOpen(true);
  };

  const handleLogoDialogOpenChange = (open: boolean) => {
    setIsLogoDialogOpen(open);
    if (!open) {
      setLogoDomain(null);
      resetLogoFormData();
    }
  };

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFormData("custom_logo", file);
      setLogoFormData("custom_logo_name", file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoFormData("custom_logo_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogoSelection = () => {
    setLogoFormData("custom_logo", null);
    setLogoFormData("custom_logo_name", "");
    setLogoFormData("custom_logo_preview", null);
  };

  const handleSubmitCustomLogo = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!logoDomain || !logoFormData.custom_logo) return;

    patchLogoFormData(domainPath(logoDomain.id), {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        setIsLogoDialogOpen(false);
        setLogoDomain(null);
        resetLogoFormData();
        toast.success("Logo updated.");
      },
      onError: () => {
        toast.error("Unable to update logo.");
      },
    });
  };

  const handleRemoveCurrentLogo = () => {
    if (!logoDomain) return;
    if (!confirm("Remove this domain's custom logo?")) return;

    router.patch(
      domainPath(logoDomain.id),
      { remove_custom_logo: true },
      {
        preserveScroll: true,
        onSuccess: () => {
          setIsLogoDialogOpen(false);
          setLogoDomain(null);
          resetLogoFormData();
          toast.success("Logo removed.");
        },
        onError: () => {
          toast.error("Unable to remove logo.");
        },
      },
    );
  };

  const handleSetCustomBrandColors = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!brandColorsDomain) return;

    patchBrandColorsFormData(domainPath(brandColorsDomain.id), {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        setIsBrandColorsDialogOpen(false);
        setBrandColorsDomain(null);
        resetBrandColorsFormData();
        toast.success("Brand colors updated.");
      },
      onError: () => {
        toast.error("Unable to update brand colors.");
      },
    });
  };

  const openBrandColorsModal = (domain: Domain) => {
    setBrandColorsDomain(domain);
    setBrandColorsFormData("bg_color_hex", domain.bg_color_hex || "");
    setBrandColorsFormData("text_color_hex", domain.text_color_hex || "");
    setIsBrandColorsDialogOpen(true);
  };

  const handleBrandColorsDialogOpenChange = (open: boolean) => {
    setIsBrandColorsDialogOpen(open);
    if (!open) {
      setBrandColorsDomain(null);
      resetBrandColorsFormData();
    }
  };

  const openVerifyDomainModal = (domain: Domain) => {
    setVerifyDomain(domain);
    setIsVerifyDialogOpen(true);
  };

  const handleVerifyDialogOpenChange = (open: boolean) => {
    setIsVerifyDialogOpen(open);
    if (!open) {
      setVerifyDomain(null);
    }
  };

  const handleVerifyDomain = () => {
    if (!verifyDomain) return;

    router.post(
      `/domains/${verifyDomain.id}/verify`,
      {},
      {
        preserveScroll: true,
        onStart: () => setIsVerifyingDomain(true),
        onSuccess: () => setIsVerifyDialogOpen(false),
        onFinish: () => setIsVerifyingDomain(false),
      },
    );
  };

  const normalizeHexOnBlur = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^[0-9a-f]{6}$/i.test(trimmed)) return `#${trimmed}`;
    return trimmed;
  };

  const ImageViewerModal = () => {
    if (!selectedImage) return null;

    return (
      <Dialog open={!!selectedImage} onOpenChange={handleImageViewClose}>
        <DialogContent className="w-full max-w-4xl overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="text-left">{selectedImage.title || "Social Image Preview"}</DialogTitle>
          </DialogHeader>
          <div className="px-6">
            <div className="bg-muted/30 relative overflow-hidden rounded-lg">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title ?? ""}
                className="h-auto max-h-[70vh] w-full object-contain"
                style={{
                  aspectRatio: "1.91/1",
                  maxWidth: "1200px",
                }}
              />
            </div>
            <div className="text-muted-foreground mt-4 text-sm">
              <p className="font-medium">URL:</p>
              <code className="bg-muted mt-1 block rounded px-2 py-1 break-all">{selectedImage.url}</code>
              {selectedImage.domain && (
                <p className="mt-2">
                  <span className="font-medium">Domain:</span>{" "}
                  <code className="bg-muted mt-1 block rounded px-2 py-1 break-all">{selectedImage.domain}</code>
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex w-full gap-2 px-6 pb-6 sm:justify-between">
            <Button variant="outline" onClick={() => handleImageViewClose()} className="flex items-center gap-2">
              <X className="h-4 w-4" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const DomainLogoModal = () => {
    if (!logoDomain) return null;

    return (
      <Dialog open={isLogoDialogOpen} onOpenChange={handleLogoDialogOpenChange}>
        <DialogContent className="w-full max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-left">Custom logo for {logoDomain.hostname}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitCustomLogo} className="space-y-4">
            <p className="text-muted-foreground text-sm">
              Upload a logo for this domain. Recommended: PNG with transparent background.
            </p>

            {logoDomain.customLogoUrl && !logoFormData.custom_logo_preview && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Current logo</p>
                <div className="relative aspect-square max-w-[160px] overflow-hidden rounded-lg border bg-transparent">
                  <img src={logoDomain.customLogoUrl} alt="Current logo" className="h-full w-full object-contain p-2" />
                </div>
              </div>
            )}

            <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
              <input
                type="file"
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
                id="domain-custom-logo-upload"
              />
              <label htmlFor="domain-custom-logo-upload" className="block cursor-pointer">
                <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                <p className="text-foreground text-sm font-medium">
                  {logoFormData.custom_logo_name ? logoFormData.custom_logo_name : "Click to upload"}
                </p>
                <p className="text-muted-foreground text-xs">PNG, JPG up to 5MB</p>
              </label>
            </div>

            {logoFormData.custom_logo_name && (
              <div className="space-y-2">
                {logoFormData.custom_logo_preview && (
                  <div className="relative aspect-square max-w-[160px] overflow-hidden rounded-lg border bg-transparent">
                    <img
                      src={logoFormData.custom_logo_preview}
                      alt="Custom logo preview"
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                )}
                <div className="bg-secondary/50 flex items-center justify-between gap-2 rounded-lg p-3">
                  <span className="text-foreground truncate text-sm">{logoFormData.custom_logo_name}</span>
                  <button
                    type="button"
                    onClick={handleRemoveLogoSelection}
                    className="hover:bg-secondary rounded p-1 transition-colors"
                  >
                    <X className="text-muted-foreground h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <DialogFooter className="flex w-full gap-2 sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleLogoDialogOpenChange(false)}
                disabled={isLogoSubmitting}
              >
                Cancel
              </Button>
              <div className="flex gap-2">
                {logoDomain.customLogoUrl && !logoFormData.custom_logo && (
                  <Button type="button" variant="outline" onClick={handleRemoveCurrentLogo} disabled={isLogoSubmitting}>
                    Remove logo
                  </Button>
                )}
                <Button type="submit" disabled={!logoFormData.custom_logo || isLogoSubmitting}>
                  {isLogoSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </span>
                  ) : (
                    "Save logo"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  const VerifyDomainModal = () => {
    if (!verifyDomain) return null;
    const [copiedRecordName, setCopiedRecordName] = useState(false);
    const [copiedRecordValue, setCopiedRecordValue] = useState(false);
    const isVerified = verifyDomain.verification_status === "verified";

    return (
      <Dialog open={isVerifyDialogOpen} onOpenChange={handleVerifyDialogOpenChange}>
        <DialogContent className="w-full max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 text-left">
              <span>Verify {verifyDomain.hostname}</span>
              <Badge variant={isVerified ? "default" : "outline"}>{isVerified ? "Verified" : "Unverified"}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Add this DNS record to your DNS provider. Once it propagates, click verify.
            </p>

            <div className="grid gap-3 rounded-lg border p-4">
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">Record type</span>
                <code className="bg-muted rounded px-2 py-1 font-mono">TXT - Text record</code>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">Host</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <code
                      className="bg-muted hover:bg-muted/80 flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 font-mono"
                      onClick={() => {
                        void navigator.clipboard.writeText(verifyDomain.verification_record_name || "");
                        setCopiedRecordName(true);
                        setTimeout(() => setCopiedRecordName(false), 2000);
                      }}
                    >
                      <span>{verifyDomain.verification_record_name}</span>
                      {copiedRecordName && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </code>
                  </TooltipTrigger>
                  <TooltipContent>{copiedRecordName ? "Copied!" : "Click to copy"}</TooltipContent>
                </Tooltip>
                <span className="text-muted-foreground text-xs">.{verifyDomain.verification_hostname}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">Answer / Value</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <code
                      className="bg-muted hover:bg-muted/80 flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 font-mono"
                      onClick={() => {
                        void navigator.clipboard.writeText(verifyDomain.verification_record_value || "");
                        setCopiedRecordValue(true);
                        setTimeout(() => setCopiedRecordValue(false), 2000);
                      }}
                    >
                      <span>{verifyDomain.verification_record_value}</span>
                      {copiedRecordValue && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </code>
                  </TooltipTrigger>
                  <TooltipContent>{copiedRecordValue ? "Copied!" : "Click to copy"}</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {verifyDomain.verification_checked_at && (
              <p className="text-muted-foreground text-xs">
                Last checked: {dateTimeFormatter.format(new Date(verifyDomain.verification_checked_at))}
              </p>
            )}
          </div>

          <DialogFooter className="flex w-full gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => handleVerifyDialogOpenChange(false)}>
              Close
            </Button>
            {!isVerified && (
              <Button onClick={handleVerifyDomain} disabled={isVerifyingDomain}>
                {isVerifyingDomain ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  "Verify now"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">Manage your domains and monitor generated OG images</p>
          </div>
          {domains.length > 0 && (
            <Button onClick={() => setIsAddingDomain(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Domain
            </Button>
          )}
        </div>

        {hasUnverifiedDomains && (
          <Alert className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100">
            <AlertTitle>Domain verification required</AlertTitle>
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>API requests won&apos;t generate OG images until your domains are verified.</span>
              {firstUnverifiedDomain && (
                <Button variant="outline" onClick={() => openVerifyDomainModal(firstUnverifiedDomain)}>
                  <CheckCircle className="h-4 w-4" />
                  Verify domain
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {active_claim?.status === "pending" && (
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50">
            <CardHeader>
              <CardTitle className="dark:text-amber-100">Domain claim pending</CardTitle>
              <CardDescription className="dark:text-amber-200/80">
                Verify ownership of {active_claim.hostname} to complete the claim.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm dark:text-amber-100">
              <div className="grid gap-3 rounded-lg border bg-white/60 p-4 dark:border-amber-800/50 dark:bg-amber-950/30">
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground">Record name</span>
                  <code className="bg-muted rounded px-2 py-1 font-mono">{active_claim.recordName}</code>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground">Record value</span>
                  <code className="bg-muted rounded px-2 py-1 font-mono">{active_claim.recordValue}</code>
                </div>
                {active_claim.expiresAt && (
                  <span className="text-muted-foreground text-xs">
                    Claim expires: {dateTimeFormatter.format(new Date(active_claim.expiresAt))}
                  </span>
                )}
              </div>
              <Button onClick={() => router.post(`/domain_claims/${active_claim.id}/verify`)}>Verify claim</Button>
            </CardContent>
          </Card>
        )}

        {/* Domains Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Registered Domains
            </CardTitle>
            <CardDescription>Your registered domains that can automatically generate OG images</CardDescription>
          </CardHeader>
          <CardContent>
            {domains.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Logo</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domains.map((domain) => {
                    return (
                      <TableRow key={domain.id}>
                        <TableCell>
                          {domain.customLogoUrl ? (
                            <img
                              src={domain.customLogoUrl}
                              alt={`${domain.hostname} logo`}
                              className="h-8 w-auto max-w-20 rounded-md border bg-transparent object-contain p-1"
                            />
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <code className="bg-muted rounded px-1.5 py-0.5 text-sm">{domain.hostname}</code>
                        </TableCell>
                        <TableCell>{getDomainVerificationBadge(domain.verification_status)}</TableCell>
                        <TableCell>{new Date(domain.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {!domain.verified_at && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    aria-label="Verify domain"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openVerifyDomainModal(domain)}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Verify domain</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  style={{
                                    backgroundColor: domain.bg_color_hex || "#FFFFFF",
                                    color: domain.text_color_hex || "#000000",
                                  }}
                                  aria-label="Set brand colors"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    openBrandColorsModal(domain);
                                  }}
                                  disabled={isBrandColorsSubmitting}
                                >
                                  <Palette className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Set domain brand colors</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  aria-label="Set custom logo"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSetCustomLogo(domain)}
                                >
                                  <ImagePlus className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Set custom logo</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  aria-label="Delete domain"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDomainDelete(domain)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Delete domain</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Globe className="text-muted-foreground mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No domains registered</h3>
                <p className="text-muted-foreground mb-4 text-center">
                  Register a domain to start automatically generating OG images for your URLs
                </p>
                <Button onClick={() => setIsAddingDomain(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Domain
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Social Images Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Generated OG Images
                </CardTitle>
                <CardDescription>
                  Your generated OG images. Use the Refine button to create new variations.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-muted-foreground">
                  {active_social_images.length.toLocaleString()} total
                </Badge>
                <InputGroup className="w-64 transition-all duration-200 focus-within:w-72">
                  <InputGroupAddon>
                    <Search className="text-muted-foreground group-focus-within/input-group:text-foreground h-4 w-4 transition-colors" />
                  </InputGroupAddon>
                  <InputGroupInput
                    ref={searchInputRef}
                    placeholder="Search images..."
                    value={search}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.currentTarget.value.length > 0 && e.key === "Escape") {
                        setSearch("");
                      } else if (e.currentTarget.value.length === 0 && e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                  <InputGroupAddon
                    align="inline-end"
                    className={cn(
                      "pointer-events-none mr-1.5 gap-0.5 opacity-60 transition-opacity",
                      search && "opacity-0",
                    )}
                  >
                    <Kbd className="border-border/50 bg-background border-border h-5 min-w-5 border px-1.5 font-mono text-sm font-bold shadow-sm dark:text-white">
                      {isMac ? "⌘" : "Ctrl"}
                    </Kbd>
                    <Kbd className="border-border/50 bg-background border-border h-5 min-w-5 border px-1.5 font-mono text-xs font-bold shadow-sm dark:text-white">
                      K
                    </Kbd>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {is_generating_new_images && (
              <div className="bg-primary/5 border-primary/20 mb-4 flex items-center gap-3 rounded-lg border p-4">
                <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                  <Loader2 className="text-primary h-5 w-5 animate-spin" />
                </div>
                <div>
                  <p className="text-sm font-medium">New images are being generated</p>
                  <p className="text-muted-foreground text-xs">
                    This page will update automatically when generation completes. This may take a few minutes.
                  </p>
                </div>
              </div>
            )}
            {active_social_images.length > 0 ? (
              <InfiniteScroll
                data="active_social_images"
                itemsElement="#active_social_images_table_body"
                preserveUrl
                onlyNext
              >
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-slate-50 dark:bg-slate-950/50">
                        <TableRow>
                          <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                            Image
                          </TableHead>
                          <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                            Details
                          </TableHead>
                          <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                            Status
                          </TableHead>
                          <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                            Page type
                          </TableHead>
                          <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                            Last updated
                          </TableHead>
                          <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody id="active_social_images_table_body">
                        {active_social_images.map((image) => {
                          return (
                            <TableRow key={image.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                              <TableCell className="min-w-[120px] px-6 py-4 align-top">
                                <div className="flex items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => handleImageView(image)}
                                    className="group hover:ring-primary relative overflow-hidden rounded-lg ring-1 ring-slate-200 transition dark:ring-slate-800"
                                    title="Click to view full size"
                                  >
                                    {image.image_url ? (
                                      <img
                                        src={image.image_url}
                                        alt={image.title ?? ""}
                                        className="h-16 w-28 object-cover"
                                      />
                                    ) : (
                                      <div className="flex h-16 w-28 items-center justify-center bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
                                        <Images className="h-5 w-5" />
                                      </div>
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
                                      <ZoomIn className="h-4 w-4 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                                    </div>
                                  </button>
                                </div>
                              </TableCell>
                              <TableCell className="px-6 py-4 align-top">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-amber-500" />
                                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                      {image.title || "Untitled image"}
                                    </span>
                                  </div>
                                  <div className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <Link2 className="mt-0.5 h-3.5 w-3.5" />
                                    <span className="break-all">{image.url}</span>
                                  </div>
                                  {image.domain && <Badge variant="outline">Domain: {image.domain}</Badge>}
                                </div>
                              </TableCell>
                              <TableCell className="px-6 py-4 align-top">{getStatusBadge(image.status)}</TableCell>
                              <TableCell className="px-6 py-4 align-top">
                                <Badge variant="outline">{formatPageType(image.page_type)}</Badge>
                              </TableCell>
                              <TableCell className="px-6 py-4 align-top">
                                {dateTimeFormatter.format(new Date(image.updated_at))}
                              </TableCell>
                              <TableCell className="px-6 py-4 align-top">
                                <div className="flex items-center gap-2">
                                  <Button
                                    aria-label="Delete social image"
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleImageDelete(image)}
                                  >
                                    <Trash className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </InfiniteScroll>
            ) : search.length > 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Search className="text-muted-foreground mb-4 h-12 w-12" aria-label="No OG images found" />
                <h3 className="mb-2 text-lg font-semibold">No OG images found</h3>
                <p className="text-muted-foreground text-center">
                  No OG images found for the search term &quot;{search}&quot;.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="bg-primary/10 mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <Zap className="text-primary h-10 w-10" />
                </div>
                <h3 className="mb-2 text-xl font-semibold">Ready to generate your first OG image!</h3>
                <p className="text-muted-foreground mb-6 max-w-md text-center">
                  Use the OG Pilot API to generate beautiful Open Graph images for your pages. Here&apos;s how to get
                  started:
                </p>

                {/* Quick Start Steps */}
                <div className="mb-6 w-full max-w-lg space-y-3">
                  <div className="bg-muted/50 flex items-start gap-3 rounded-lg p-4">
                    <span className="bg-primary text-primary-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium">
                      1
                    </span>
                    <div>
                      <p className="font-medium">Get your API key</p>
                      <p className="text-muted-foreground text-sm">
                        Go to{" "}
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={() => router.visit("/api_keys")}
                        >
                          API Keys
                        </button>{" "}
                        to create or view your API key.
                      </p>
                    </div>
                  </div>

                  <div className="bg-muted/50 flex items-start gap-3 rounded-lg p-4">
                    <span className="bg-primary text-primary-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium">
                      2
                    </span>
                    <div>
                      <p className="font-medium">Sign a JWT and call the API server-side</p>
                      <p className="text-muted-foreground text-sm">
                        Sign a JWT with your API key (HS256), send it to the API, then set the returned CDN URL in your
                        og:image meta tag:
                      </p>
                      <pre className="bg-background mt-2 overflow-x-auto rounded border p-2 text-xs whitespace-pre-wrap">
                        {`curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'`}
                      </pre>
                    </div>
                  </div>

                  <div className="bg-muted/50 flex items-start gap-3 rounded-lg p-4">
                    <span className="bg-primary text-primary-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium">
                      3
                    </span>
                    <div>
                      <p className="font-medium">Images appear here automatically</p>
                      <p className="text-muted-foreground text-sm">
                        When someone shares your page on social media, the image will be generated and stored here for
                        management.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={() => router.visit("/docs")}>
                    <BookOpen className="mr-2 h-4 w-4" />
                    Read Documentation
                  </Button>
                  <Button variant="outline" onClick={() => router.visit("/api_keys")}>
                    <Key className="mr-2 h-4 w-4" />
                    Manage API Keys
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Domain Modal */}
        <AddDomainModal open={isAddingDomain} onOpenChange={setIsAddingDomain} />

        {/* Domain Brand Colors Modal */}
        {brandColorsDomain && (
          <Dialog open={isBrandColorsDialogOpen} onOpenChange={handleBrandColorsDialogOpenChange}>
            <DialogContent className="w-full max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-left">Brand colors for {brandColorsDomain.hostname}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSetCustomBrandColors} className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Set custom colors used across previews for this domain. Leave a field blank to use the default
                  styling.
                </p>

                {(() => {
                  const bgValid =
                    !brandColorsFormData.bg_color_hex || HEX_COLOR_RE.test(brandColorsFormData.bg_color_hex);
                  const textValid =
                    !brandColorsFormData.text_color_hex || HEX_COLOR_RE.test(brandColorsFormData.text_color_hex);

                  const previewBg =
                    bgValid && brandColorsFormData.bg_color_hex ? brandColorsFormData.bg_color_hex : "#0B1220";
                  const previewText =
                    textValid && brandColorsFormData.text_color_hex ? brandColorsFormData.text_color_hex : "#FFFFFF";

                  const canSubmit = bgValid && textValid && !isBrandColorsSubmitting;

                  return (
                    <>
                      <div
                        className="rounded-lg border p-4 transition-colors"
                        style={{
                          backgroundColor: previewBg,
                          color: previewText,
                        }}
                      >
                        <p className="text-sm font-semibold">Preview</p>
                        <p className="text-sm opacity-90">This is how text will look on the chosen background.</p>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="domain-bg-color-hex">Background</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              aria-label="Background color picker"
                              type="color"
                              value={brandColorsFormData.bg_color_hex || "#0B1220"}
                              onChange={(e) => setBrandColorsFormData("bg_color_hex", e.target.value)}
                              className="h-9 w-12 cursor-pointer p-1"
                            />
                            <Input
                              id="domain-bg-color-hex"
                              value={brandColorsFormData.bg_color_hex}
                              placeholder="#0B1220"
                              onBlur={() =>
                                setBrandColorsFormData(
                                  "bg_color_hex",
                                  normalizeHexOnBlur(brandColorsFormData.bg_color_hex),
                                )
                              }
                              onChange={(e) => setBrandColorsFormData("bg_color_hex", e.target.value)}
                              aria-invalid={!bgValid}
                            />
                          </div>
                          {!bgValid && <p className="text-destructive text-xs">Use #RRGGBB.</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="domain-text-color-hex">Text</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              aria-label="Text color picker"
                              type="color"
                              value={brandColorsFormData.text_color_hex || "#FFFFFF"}
                              onChange={(e) => setBrandColorsFormData("text_color_hex", e.target.value)}
                              className="h-9 w-12 cursor-pointer p-1"
                            />
                            <Input
                              id="domain-text-color-hex"
                              value={brandColorsFormData.text_color_hex}
                              placeholder="#FFFFFF"
                              onChange={(e) => setBrandColorsFormData("text_color_hex", e.target.value)}
                              onBlur={() =>
                                setBrandColorsFormData(
                                  "text_color_hex",
                                  normalizeHexOnBlur(brandColorsFormData.text_color_hex),
                                )
                              }
                              aria-invalid={!textValid}
                            />
                          </div>
                          {!textValid && <p className="text-destructive text-xs">Use #RRGGBB.</p>}
                        </div>
                      </div>

                      <DialogFooter className="flex w-full gap-2 sm:justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleBrandColorsDialogOpenChange(false)}
                          disabled={isBrandColorsSubmitting}
                        >
                          Cancel
                        </Button>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setBrandColorsFormData("bg_color_hex", "");
                              setBrandColorsFormData("text_color_hex", "");
                            }}
                            disabled={isBrandColorsSubmitting}
                          >
                            Reset
                          </Button>
                          <Button type="submit" disabled={!canSubmit}>
                            {isBrandColorsSubmitting ? (
                              <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving…
                              </span>
                            ) : (
                              "Save colors"
                            )}
                          </Button>
                        </div>
                      </DialogFooter>
                    </>
                  );
                })()}
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Domain Logo Modal */}
        <DomainLogoModal />

        {/* Domain Verification Modal */}
        <VerifyDomainModal />

        {/* Image Viewer Modal */}
        <ImageViewerModal />
      </div>
    </AppLayout>
  );
}
